/**
 * Whether a month's utility statements are all in — and the place to file them.
 *
 * The dispensing partner sends one gas bill per fuelling station alongside the
 * allocation workbook. They are the support a verifier reads to believe the
 * station volumes on the ledger above, and nothing recorded whether they had
 * arrived until this card.
 *
 * ## The meter number is typed, never shown
 *
 * A missing-statement row names the stations the bill covers and stops there.
 * The backend knows which meter it wants, but printing it would turn the check
 * into a transcription of the app's own answer — the operator would read the
 * screen instead of the bill, which is exactly the failure the control exists to
 * catch. A mismatch is where both numbers appear, because by then the
 * discrepancy is real and wants a person's decision.
 *
 * ## There is nothing to press
 *
 * The verdict is derived on every read, so opening the ledger is what produces
 * the findings. A "Run validation" button briefly sat here and was removed: it
 * returned the same payload, rendered no feedback at all, and borrowed its name
 * from the batch screen, where validation genuinely runs something. It read as
 * though the findings were its output when they were already on screen.
 *
 * ## Why one bill can answer several rows
 *
 * The requirement is one statement per gas METER, not per station: several
 * stations dispense on one meter and are billed together. So filing against any
 * station on a meter clears every row behind it, and the counts on this card are
 * meters rather than stations.
 */

import {
  Alert,
  Badge,
  Button,
  Group,
  Paper,
  Stack,
  Table,
  Text,
  TextInput,
  Tooltip,
} from "@mantine/core";
import { Dropzone } from "@mantine/dropzone";
import { IconAlertTriangle, IconCheck, IconFileText, IconUpload, IconX } from "@tabler/icons-react";
import { useState } from "react";
import {
  useFileUtilityStatement,
  useLcfsPeriodStatements,
  useLcfsPeriodValidation,
} from "../../api/hooks";
import type { LcfsPeriodFinding, LcfsPeriodValidation } from "../../api/types";
import { periodLabelFrom } from "../../lib/datetime";
import { formatApiError } from "../../lib/errors";
import { ReasonModal } from "../ReasonModal";

/** Colour and words for a period's verdict. */
const STATUS: Record<
  LcfsPeriodValidation["status"],
  { color: string; label: (v: LcfsPeriodValidation) => string }
> = {
  passed: { color: "teal", label: () => "Support complete" },
  failed: {
    color: "red",
    label: (v) => (v.findings.length === 1 ? "1 issue" : `${v.findings.length} issues`),
  },
  // Not a pass. These months predate the first one the partner sent statements
  // for, so there is nothing to grade — grey, never green.
  not_required: { color: "gray", label: () => "Support not required" },
};

export function PeriodSupportBadge({ period }: { period: string | undefined }) {
  const q = useLcfsPeriodValidation(period);
  if (!period || q.isPending || !q.data) return null;
  const { color, label } = STATUS[q.data.status];
  return (
    <Badge color={color} variant="light" radius="sm">
      {label(q.data)}
    </Badge>
  );
}

export function PeriodSupportCard({ period }: { period: string | undefined }) {
  const validationQ = useLcfsPeriodValidation(period);
  const statementsQ = useLcfsPeriodStatements(period);
  const verdict = validationQ.data;

  if (!period) return null;

  return (
    <Paper p="md" radius="md" withBorder data-test="period-support">
      <Stack gap="md">
        <Group justify="space-between" wrap="wrap" gap="sm" align="flex-start">
          <Stack gap={2} maw={620}>
            <Text fw={600} size="sm">
              Utility statements for {periodLabelFrom(period)}
            </Text>
            <Text size="xs" c="dimmed">
              One gas bill per station, sent with the ledger. Stations billed on the same meter
              share a bill, so a month needs one statement per meter — not one per station. A
              statement dropped here is filed as {periodLabelFrom(period)} support.
            </Text>
          </Stack>
          {verdict ? <PeriodSupportBadge period={period} /> : null}
        </Group>

        {validationQ.isError ? (
          <Alert color="red" variant="light" icon={<IconAlertTriangle size={16} />}>
            <Text size="sm">{formatApiError(validationQ.error)}</Text>
          </Alert>
        ) : null}

        {verdict ? <Verdict verdict={verdict} period={period} /> : null}

        {statementsQ.data && statementsQ.data.length > 0 ? (
          <FiledTable rows={statementsQ.data} />
        ) : null}
      </Stack>
    </Paper>
  );
}

function Verdict({
  verdict,
  period,
}: {
  verdict: LcfsPeriodValidation;
  period: string;
}) {
  if (verdict.status === "not_required") {
    return (
      <Text size="sm" c="dimmed">
        No statements were collected for this month, so its support is not graded.
      </Text>
    );
  }
  if (verdict.status === "passed") {
    return (
      <Group gap="xs">
        <IconCheck size={16} color="var(--mantine-color-teal-6)" />
        <Text size="sm">All {verdict.required_statements} statements filed.</Text>
      </Group>
    );
  }
  return (
    <Stack gap="sm">
      <Text size="sm" c="dimmed">
        {verdict.filed_statements} of {verdict.required_statements} filed.
      </Text>
      {verdict.findings.map((finding) => (
        <FindingRow
          key={`${finding.code}:${finding.gas_meter_number ?? finding.fse_ids[0]}`}
          finding={finding}
          period={period}
        />
      ))}
    </Stack>
  );
}

function FindingRow({
  finding,
  period,
}: {
  finding: LcfsPeriodFinding;
  period: string;
}) {
  const fileable = finding.code === "statement_missing";
  return (
    <Paper
      p="sm"
      radius="sm"
      withBorder
      style={{ borderLeft: "3px solid var(--mantine-color-red-6)" }}
    >
      <Stack gap="xs">
        <Text size="sm">{finding.message}</Text>
        {fileable ? <FileStatement finding={finding} period={period} /> : null}
      </Stack>
    </Paper>
  );
}

/**
 * Drop the bill, type the gas meter number off it, file.
 *
 * `fse_ids[0]` is what the statement is filed against — any station on the meter
 * will do, because the row the backend writes is keyed on the meter and covers
 * all of them.
 */
function FileStatement({
  finding,
  period,
}: {
  finding: LcfsPeriodFinding;
  period: string;
}) {
  const [file, setFile] = useState<File | null>(null);
  const [meter, setMeter] = useState("");
  const [reasonOpen, setReasonOpen] = useState(false);
  const fileStatement = useFileUtilityStatement();

  const reset = () => {
    setFile(null);
    setMeter("");
  };

  return (
    <Stack gap="xs">
      {!file ? (
        <Dropzone
          onDrop={(accepted) => setFile(accepted[0] ?? null)}
          accept={{ "application/pdf": [".pdf"] }}
          maxFiles={1}
          maxSize={10 * 1024 * 1024}
        >
          <Group justify="center" gap="sm" mih={52} style={{ pointerEvents: "none" }}>
            <Dropzone.Accept>
              <IconUpload size={18} />
            </Dropzone.Accept>
            <Dropzone.Reject>
              <IconX size={18} />
            </Dropzone.Reject>
            <Dropzone.Idle>
              <IconFileText size={18} />
            </Dropzone.Idle>
            <Text size="xs" c="dimmed">
              Drop this station's statement (PDF)
            </Text>
          </Group>
        </Dropzone>
      ) : (
        <Group gap="xs" align="flex-end" wrap="wrap">
          <Badge variant="light" radius="sm" maw={280}>
            {file.name}
          </Badge>
          <Tooltip
            label="The meter on the GAS service. These bills also carry an electric meter and other identifiers."
            multiline
            w={260}
            withArrow
          >
            <TextInput
              size="xs"
              w={190}
              label="Gas meter number"
              placeholder="read it off the bill"
              value={meter}
              onChange={(e) => setMeter(e.currentTarget.value)}
            />
          </Tooltip>
          <Button
            size="compact-sm"
            disabled={meter.trim().length === 0}
            loading={fileStatement.isPending}
            onClick={() => setReasonOpen(true)}
          >
            File
          </Button>
          <Button size="compact-sm" variant="subtle" onClick={reset}>
            Choose a different file
          </Button>
        </Group>
      )}

      {/* No alert here: the modal stays open on a refusal and renders the error
          itself, so a second copy of the meter mismatch would say it twice. */}
      <ReasonModal
        opened={reasonOpen}
        title="File utility statement"
        description={`File this statement as ${period} support for ${finding.station_names[0] ?? finding.fse_ids[0]}.`}
        confirmLabel="File statement"
        busy={fileStatement.isPending}
        error={fileStatement.error}
        onConfirm={(reason) => {
          if (!file) return;
          fileStatement.mutate(
            {
              file,
              period,
              fseId: finding.fse_ids[0],
              gasMeterNumber: meter.trim(),
              reason,
            },
            {
              onSuccess: () => {
                setReasonOpen(false);
                reset();
              },
            },
          );
        }}
        onClose={() => setReasonOpen(false)}
      />
    </Stack>
  );
}

function FiledTable({
  rows,
}: {
  rows: NonNullable<ReturnType<typeof useLcfsPeriodStatements>["data"]>;
}) {
  return (
    <Stack gap="xs">
      <Text size="xs" c="dimmed" fw={600}>
        Filed
      </Text>
      <Table highlightOnHover verticalSpacing="xs" fz="xs">
        <Table.Thead>
          <Table.Tr>
            <Table.Th>Gas meter</Table.Th>
            <Table.Th>Utility</Table.Th>
            <Table.Th>Billing period</Table.Th>
            <Table.Th>File</Table.Th>
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {rows.map((row) => (
            <Table.Tr key={row.id}>
              <Table.Td ff="monospace">{row.gas_meter_number}</Table.Td>
              <Table.Td>{row.utility ?? "—"}</Table.Td>
              {/* Shown, never graded: it is usually not the calendar month. */}
              <Table.Td>
                {row.statement_start && row.statement_end
                  ? `${row.statement_start} — ${row.statement_end}`
                  : "—"}
              </Table.Td>
              <Table.Td>{row.original_filename}</Table.Td>
            </Table.Tr>
          ))}
        </Table.Tbody>
      </Table>
    </Stack>
  );
}
